import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Sparkles, Heart, MessageCircle, Send, Wrench, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

const EditorsLetter = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("This week in Le Salon");
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [stuckCount, setStuckCount] = useState(0);
  const [fixingStuck, setFixingStuck] = useState(false);
  const [purgingStuck, setPurgingStuck] = useState(false);

  useEffect(() => {
    api.get("/featured").then(({ data }) => {
      setPosts(data);
      setLoading(false);
    });
  }, []);

  // Editor-only: check for stuck broadcasts (missed Mux webhooks).
  // Polls every 60 seconds so a stuck broadcast lit up from another device
  // shows up without a page reload.
  useEffect(() => {
    if (!user?.is_editor) return;
    let cancelled = false;
    const fetchStuck = async () => {
      try {
        const { data } = await api.get("/live/broadcasts/stuck");
        if (!cancelled) setStuckCount(data?.count || 0);
      } catch { /* ignore transient errors */ }
    };
    fetchStuck();
    const timer = setInterval(fetchStuck, 60_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user?.is_editor]);

  const fixStuckBroadcasts = async () => {
    setFixingStuck(true);
    try {
      const { data } = await api.post("/live/broadcasts/sync-all");
      if (data.recovered > 0) {
        toast.success(
          `Recovered ${data.recovered} of ${data.checked} — they're playable now.`
        );
      } else if (data.checked === 0) {
        toast("Nothing to fix — everything looks good.");
      } else {
        toast(
          `Checked ${data.checked} — none ready yet. Try again in a minute, or use "Delete unrecoverable" if the source dropped before recording anything.`
        );
      }
      // Refresh the count
      const { data: fresh } = await api.get("/live/broadcasts/stuck");
      setStuckCount(fresh?.count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sync broadcasts.");
    } finally {
      setFixingStuck(false);
    }
  };

  const purgeUnrecoverable = async () => {
    if (!window.confirm(
      "Delete stuck broadcasts that have no video?\n\n" +
      "These are broadcasts where Larix (or the source) dropped before Mux could encode a video file — there is no footage to recover. This is safe and only removes broadcasts that will never play."
    )) return;
    setPurgingStuck(true);
    try {
      const { data } = await api.post("/live/broadcasts/stuck/purge");
      if (data.purged_count > 0) {
        toast.success(`Deleted ${data.purged_count} unrecoverable broadcast${data.purged_count === 1 ? "" : "s"}.`);
      } else {
        toast("Nothing to delete — everything either has video or is still recoverable.");
      }
      const { data: fresh } = await api.get("/live/broadcasts/stuck");
      setStuckCount(fresh?.count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed.");
    } finally {
      setPurgingStuck(false);
    }
  };

  const broadcast = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/broadcast", { subject, intro_html: "" });
      toast.success(`Sent to ${data.sent} of ${data.subscribers} subscribers.`);
      setShowSendDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const hero = posts[0];
  const rest = posts.slice(1);

  return (
    <div data-testid="editors-letter-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />

      {/* MASTHEAD */}
      <div style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-default)" }}>
        <div className="container-belle" style={{ paddingTop: 130, paddingBottom: 64, textAlign: "center", maxWidth: 880 }}>
          <div className="overline" style={{ color: "var(--brand-accent)", display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
            <Sparkles size={14} strokeWidth={1.4} /> The Editor&apos;s Journal · Nº 1
          </div>
          <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 18, marginBottom: 20 }}>
            The Editor&apos;s Journal
          </h1>
          <p className="font-serif" style={{ fontSize: 22, fontStyle: "italic", color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
            Small updates and reflections from the editor&apos;s desk.
          </p>
          {user?.is_editor && (
            <div style={{ marginTop: 32, display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <button
                data-testid="send-letter-button"
                onClick={() => setShowSendDialog(true)}
                className="btn-accent"
                style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
              >
                <Send size={14} strokeWidth={1.5} /> Send this letter to subscribers
              </button>
              <span className="overline" style={{ opacity: 0.6 }}>Editor only</span>
              {stuckCount > 0 && (
                <div style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "center", gap: 10, marginTop: 8 }}>
                  <button
                    data-testid="fix-stuck-broadcasts-button"
                    onClick={fixStuckBroadcasts}
                    disabled={fixingStuck || purgingStuck}
                    title="Batch-syncs any broadcast where Mux's webhook was missed. Safe to click any time."
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 16px",
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      fontFamily: "'Inter', sans-serif",
                      border: "1px solid var(--brand-accent)",
                      borderRadius: 24,
                      background: "rgba(120, 34, 45, 0.06)",
                      color: "var(--brand-accent)",
                      cursor: (fixingStuck || purgingStuck) ? "wait" : "pointer",
                      opacity: (fixingStuck || purgingStuck) ? 0.7 : 1,
                    }}
                  >
                    <Wrench size={12} strokeWidth={1.6} />
                    {fixingStuck
                      ? "Fixing…"
                      : `${stuckCount} stuck — click to try recovering`}
                  </button>
                  <button
                    data-testid="purge-stuck-broadcasts-button"
                    onClick={purgeUnrecoverable}
                    disabled={fixingStuck || purgingStuck}
                    title="Deletes stuck broadcasts that have no video (unrecoverable). Only removes broadcasts that will never play."
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 16px",
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      fontFamily: "'Inter', sans-serif",
                      border: "1px solid #B33636",
                      borderRadius: 24,
                      background: "rgba(179, 54, 54, 0.06)",
                      color: "#B33636",
                      cursor: (fixingStuck || purgingStuck) ? "wait" : "pointer",
                      opacity: (fixingStuck || purgingStuck) ? 0.7 : 1,
                    }}
                  >
                    <Trash2 size={12} strokeWidth={1.6} />
                    {purgingStuck ? "Deleting…" : "Delete unrecoverable"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showSendDialog && (
        <div data-testid="send-letter-dialog" style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.6)", zIndex: 200, display: "grid", placeItems: "center", padding: 24 }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border-default)", maxWidth: 520, width: "100%", padding: 40 }}>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Send this week's letter</div>
            <h2 className="editorial-h1" style={{ fontSize: 36, marginTop: 12, marginBottom: 20 }}>To the subscribers.</h2>
            <p className="editorial-body" style={{ fontSize: 15, marginBottom: 24 }}>
              The featured posts above will appear in the email, with your editor notes attached. Choose a subject line.
            </p>
            <input
              data-testid="letter-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input-belle"
              placeholder="Subject line"
              style={{ marginBottom: 24 }}
            />
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={broadcast} disabled={sending} className="btn-accent" data-testid="confirm-send-button">
                {sending ? "Sending…" : `Send to all subscribers`}
              </button>
              <button onClick={() => setShowSendDialog(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="container-belle" style={{ paddingTop: 64, paddingBottom: 32 }}>
        {loading && <p style={{ color: "var(--text-muted)" }}>Loading…</p>}

        {!loading && posts.length === 0 && (
          <div style={{ padding: 64, border: "1px solid var(--border-default)", textAlign: "center", maxWidth: 640, margin: "0 auto" }}>
            <p className="font-serif" style={{ fontStyle: "italic", fontSize: 24, lineHeight: 1.4, margin: 0 }}>
              The editor is still curating this week's letter. Return Sunday.
            </p>
          </div>
        )}

        {/* FEATURED HERO */}
        {hero && (
          <article style={{ marginBottom: 80, display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 64, alignItems: "center" }} className="hero-feature">
            <Link to={`/post/${hero.id}`} className="media-zoom" style={{ overflow: "hidden", aspectRatio: "4/5", display: "block" }}>
              <img loading="lazy" decoding="async" src={hero.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Link>
            <div>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>This week's letter · From the editor</div>

              {/* Editor's note - the pull quote */}
              {hero.editor_note && (
                <div className="pull-quote font-serif" style={{ fontSize: 26, color: "var(--text-primary)", margin: "24px 0 32px" }}>
                  {hero.editor_note}
                </div>
              )}

              {hero.post_type === "blog" ? (
                <>
                  <h2 className="editorial-h1" style={{ fontSize: "clamp(34px, 5vw, 56px)", marginTop: 0, marginBottom: 20 }}>
                    {hero.title}
                  </h2>
                  <p className="editorial-body" style={{ fontSize: 17, marginBottom: 24 }}>
                    {hero.body?.slice(0, 220)}…
                  </p>
                </>
              ) : (
                <p className="font-serif" style={{ fontSize: 24, fontStyle: "italic", lineHeight: 1.45, marginTop: 0, marginBottom: 24 }}>
                  "{hero.caption}"
                </p>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                {hero.author?.avatar_url && (
                  <img loading="lazy" decoding="async" src={hero.author.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }} />
                )}
                <div>
                  <Link to={`/profile/${hero.author?.username}`} className="font-serif" style={{ fontSize: 17, fontWeight: 500 }}>
                    {hero.author?.display_name}
                  </Link>
                  <div className="overline" style={{ marginTop: 2 }}>
                    {hero.author?.location} · {formatDate(hero.featured_at || hero.created_at)}
                  </div>
                </div>
              </div>

              <Link to={`/post/${hero.id}`} className="btn-primary" style={{ textDecoration: "none" }}>
                {hero.post_type === "blog" ? "Read the letter" : "View the photograph"}
              </Link>
            </div>
          </article>
        )}

        {/* REST OF THE LETTER */}
        {rest.length > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 40 }}>
              <div className="overline">Also chosen this week</div>
              <div style={{ flex: 1, height: 1, background: "var(--border-default)" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 48 }}>
              {rest.map((p) => (
                <article key={p.id} data-testid={`featured-${p.id}`} className="card-belle-hover">
                  <Link to={`/post/${p.id}`} className="media-zoom" style={{ display: "block", aspectRatio: "5/4", overflow: "hidden", marginBottom: 16 }}>
                    <img loading="lazy" decoding="async" src={p.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </Link>
                  {p.editor_note && (
                    <p className="font-serif" style={{ fontStyle: "italic", fontSize: 16, lineHeight: 1.5, color: "var(--brand-accent)", borderLeft: "2px solid var(--brand-accent)", paddingLeft: 14, margin: "0 0 14px" }}>
                      {p.editor_note}
                    </p>
                  )}
                  <Link to={`/post/${p.id}`}>
                    {p.post_type === "blog" ? (
                      <h3 className="font-serif" style={{ fontSize: 24, lineHeight: 1.25, marginTop: 8, marginBottom: 10, fontWeight: 500 }}>
                        {p.title}
                      </h3>
                    ) : (
                      <p className="font-serif" style={{ fontSize: 18, fontStyle: "italic", lineHeight: 1.5, margin: "0 0 12px" }}>
                        "{p.caption?.slice(0, 130)}"
                      </p>
                    )}
                  </Link>
                  <div className="overline">By {p.author?.display_name} · {p.author?.location}</div>
                  <div style={{ display: "flex", gap: 18, marginTop: 14, color: "var(--text-muted)", fontSize: 13 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Heart size={14} strokeWidth={1.4} /> {p.likes_count}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}><MessageCircle size={14} strokeWidth={1.4} /> {p.comments_count}</span>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>

      <NewsletterSignup variant="full" />
      <Footer />

      <style>{`
        @media (max-width: 900px) {
          .hero-feature { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </div>
  );
};

export default EditorsLetter;
